
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, ExternalLink, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';

interface CategoryData {
  category_name: string;
  profile_content: any;
  processing_status: 'completed' | 'failed';
  error_message?: string;
  source_summary_ids?: string[];
  ordered_article_summaries?: ArticleSummary[];
}

interface ArticleSummary {
  id: string;
  summary_text: string;
  categories?: any;
  relevance_score?: number;
  article_order: number;
  article_url?: string;
  title?: string;
  sources?: Array<{
    url: string;
    title: string;
    relevance_score?: number;
  }>;
}

interface KYPResultsSectionProps {
  completedCategories: CategoryData[];
  categoryStatuses: Record<string, boolean>;
  isPolling: boolean;
  selectedCategory?: string;
  onCategoryChange?: (categoryName: string) => void;
}

const categoryDisplayNames: Record<string, string> = {
  family: 'Family & Relationships',
  financial: 'Financial',
  career: 'Career',
  personal_life: 'Personal Life',
  agent: 'Agent',
  personality: 'Personality',
  early_career_and_education: 'Early Career & Education',
  adverse_media: 'Adverse Media',
  legal_and_criminal_history: 'Legal & Criminal History',
  sponsorships: 'Sponsorships',
  'on-field_comments': 'On-Field Comments'
};

const allCategories = [
  'family',
  'financial', 
  'career',
  'personal_life',
  'agent',
  'personality',
  'early_career_and_education',
  'adverse_media',
  'legal_and_criminal_history',
  'sponsorships',
  'on-field_comments'
];

const getRatingColor = (score: number): string => {
  if (score >= 8) return 'bg-green-500';
  if (score >= 6) return 'bg-yellow-500';
  if (score >= 4) return 'bg-orange-500';
  return 'bg-red-500';
};

const formatCategoryName = (name: string): string => {
  return categoryDisplayNames[name] || name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Create a mapping function to match frontend category names with backend names
const findMatchingCategory = (frontendCategoryName: string, completedCategories: CategoryData[]): CategoryData | undefined => {
  // First try exact match
  let match = completedCategories.find(c => c.category_name === frontendCategoryName);
  if (match) return match;
  
  // Try case-insensitive match
  match = completedCategories.find(c => c.category_name.toLowerCase() === frontendCategoryName.toLowerCase());
  if (match) return match;
  
  // Try fuzzy matching for known mappings
  const mappings: Record<string, string[]> = {
    'family': ['family_and_relationships', 'family'],
    'financial': ['financial'],
    'career': ['career'],
    'personal_life': ['personal_life'],
    'agent': ['agent'],
    'personality': ['personality'],
    'early_career_and_education': ['early_career_and_youth', 'early_career_and_education'],
    'adverse_media': ['adverse_media'],
    'legal_and_criminal_history': ['legal_criminal_or_compliance_issues', 'legal_and_criminal_history'],
    'sponsorships': ['sponsorships_and_commercial', 'sponsorships'],
    'on-field_comments': ['on_field_comments', 'on-field_comments']
  };
  
  const possibleNames = mappings[frontendCategoryName] || [];
  
  for (const possibleName of possibleNames) {
    match = completedCategories.find(c => 
      c.category_name.toLowerCase() === possibleName.toLowerCase() ||
      c.category_name.toLowerCase().replace(/[_-]/g, '') === possibleName.toLowerCase().replace(/[_-]/g, '')
    );
    if (match) return match;
  }
  
  // Last resort: partial matching
  const normalizedFrontend = frontendCategoryName.toLowerCase().replace(/[_-]/g, '');
  match = completedCategories.find(c => {
    const normalizedBackend = c.category_name.toLowerCase().replace(/[_-]/g, '');
    return normalizedBackend.includes(normalizedFrontend.split('_')[0]) || 
           normalizedFrontend.includes(normalizedBackend.split('_')[0]);
  });
  
  return match;
};

export const KYPResultsSection: React.FC<KYPResultsSectionProps> = ({
  completedCategories,
  categoryStatuses,
  isPolling,
  selectedCategory: externalSelectedCategory,
  onCategoryChange
}) => {
  // Debug logging
  console.log('[KYPResultsSection] completedCategories:', completedCategories.map(c => c.category_name));
  console.log('[KYPResultsSection] categoryStatuses keys:', Object.keys(categoryStatuses));
  const [internalSelectedCategory, setInternalSelectedCategory] = useState<string>(
    allCategories[0]
  );
  
  // Use external selected category if provided, otherwise use internal state
  const selectedCategory = externalSelectedCategory || internalSelectedCategory;
  
  const handleCategoryChange = (categoryName: string) => {
    setInternalSelectedCategory(categoryName);
    if (onCategoryChange) {
      onCategoryChange(categoryName);
    }
  };
  const [expandedRating, setExpandedRating] = useState(false);
  const [expandedSummaries, setExpandedSummaries] = useState<Record<string, boolean>>({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [showExpandedContent, setShowExpandedContent] = useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Handle hover state changes with delays
  const handleMouseEnter = React.useCallback(() => {
    setIsHovering(true);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowExpandedContent(true);
    }, 250);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovering(false);
    setShowExpandedContent(false);
  }, []);

  // Don't show if no polling has started
  if (!isPolling && completedCategories.length === 0) {
    return null;
  }

  const currentCategory = findMatchingCategory(selectedCategory, completedCategories);
  let currentProfileContent = null;
  
  if (currentCategory?.profile_content) {
    try {
      // Check if profile_content is already an object or needs parsing
      currentProfileContent = typeof currentCategory.profile_content === 'string' 
        ? JSON.parse(currentCategory.profile_content)
        : currentCategory.profile_content;
    } catch (e) {
      console.error('Error parsing profile content:', e, 'Raw content:', currentCategory.profile_content);
    }
  }

  const currentSummaries = currentCategory?.ordered_article_summaries || [];
  const isLoadingCurrentSummaries = false; // No loading needed since data is embedded

  const toggleSummary = (summaryId: string) => {
    setExpandedSummaries(prev => ({
      ...prev,
      [summaryId]: !prev[summaryId]
    }));
  };

  // Note: Article summaries are now embedded in category data, no loading needed

  return (
    <div className="bg-[#1A1A1A] rounded-lg border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-[#22333B]">
        <h2 className="text-xl font-semibold text-[#C6AC8E]">KYP Analysis Results</h2>
        <p className="text-[#EAE0D5] text-sm mt-1">
          Detailed analysis across {completedCategories.length} categories
        </p>
      </div>

      <div className="flex h-[900px]">
        {/* Left Sidebar - Category Navigation */}
        <motion.div 
          className={cn(
            "border-r border-[#22333B] bg-[#161616] overflow-y-auto relative"
          )}
          initial={false}
          animate={{ width: (sidebarCollapsed && !isHovering) ? 64 : 320 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="p-4">
            {showExpandedContent && (
              <h3 className="text-sm font-medium text-[#C6AC8E] mb-3">Categories</h3>
            )}
            <div className="space-y-1">
              {allCategories.map((categoryName) => {
                const completedCategory = findMatchingCategory(categoryName, completedCategories);
                const isCompleted = !!completedCategory;
                const isSelected = categoryName === selectedCategory;
                
                let rating = 0;
                let profileContent = null;
                
                if (completedCategory?.profile_content) {
                  try {
                    // Check if profile_content is already an object or needs parsing
                    profileContent = typeof completedCategory.profile_content === 'string' 
                      ? JSON.parse(completedCategory.profile_content)
                      : completedCategory.profile_content;
                    rating = profileContent?.rating?.score || 0;
                  } catch (e) {
                    console.error('Error parsing profile content:', e, 'Raw content:', completedCategory.profile_content);
                  }
                }
                
                // Debug logging for each category
                console.log(`[KYPResultsSection] ${categoryName}:`, {
                  hasCompletedCategory: !!completedCategory,
                  backendCategoryName: completedCategory?.category_name,
                  isCompleted,
                  rating
                });

                return (
                  <motion.button
                    key={categoryName}
                    onClick={() => handleCategoryChange(categoryName)}
                    disabled={!isCompleted}
                    className={cn(
                      "w-full text-left rounded-lg transition-all duration-200 group",
                      (sidebarCollapsed && !isHovering) ? "p-2" : "p-3",
                      isSelected 
                        ? "bg-[#e7c887] text-[#22333B]" 
                        : isCompleted
                          ? "hover:bg-[#22333B] text-[#EAE0D5]"
                          : "text-gray-500 cursor-not-allowed bg-gray-900"
                    )}
                    whileHover={isCompleted ? { scale: 1.02 } : {}}
                    whileTap={isCompleted ? { scale: 0.98 } : {}}
                    title={(sidebarCollapsed && !showExpandedContent) ? formatCategoryName(categoryName) : undefined}
                  >
                    {(sidebarCollapsed && !showExpandedContent) ? (
                      <div className="flex items-center justify-center">
                        <div className={cn(
                          "w-8 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white",
                          isCompleted ? getRatingColor(rating) : "bg-gray-600"
                        )}>
                          {isCompleted ? rating : '?'}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <span className={cn(
                          "font-medium text-sm",
                          isSelected ? "text-[#22333B] group-hover:text-[#EAE0D5]" : "text-[#EAE0D5]"
                        )}>
                          {formatCategoryName(categoryName)}
                        </span>
                        <div className="flex items-center gap-2">
                          {!isCompleted && (
                            <Loader className="h-3 w-3 animate-spin text-gray-500" />
                          )}
                          <div className={cn(
                            "w-8 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white",
                            isCompleted ? getRatingColor(rating) : "bg-gray-600"
                          )}>
                            {isCompleted ? rating : '?'}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentCategory && currentProfileContent ? (
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-6"
              >
                {/* Completed Category Content */}
                {/* Category Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-[#C6AC8E]">
                      {formatCategoryName(selectedCategory)}
                    </h3>
                    <div className={cn(
                      "px-3 py-1 rounded-full text-sm font-bold text-white",
                      getRatingColor(currentProfileContent.rating.score)
                    )}>
                      {currentProfileContent.rating.score}/10
                    </div>
                  </div>
                  
                  {/* Rating Explanation */}
                  <motion.div
                    className="border border-gray-600 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedRating(!expandedRating)}
                      className="w-full p-3 bg-[#22333B] hover:bg-[#5E503F] transition-colors flex items-center justify-between text-left"
                    >
                      <span className="text-sm font-medium text-[#EAE0D5]">Rating Explanation</span>
                      {expandedRating ? (
                        <ChevronUp className="h-4 w-4 text-[#C6AC8E]" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-[#C6AC8E]" />
                      )}
                    </button>
                    <AnimatePresence>
                      {expandedRating && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="p-3 bg-[#1A1A1A] text-sm text-[#EAE0D5] leading-relaxed kyp-markdown">
                            <ReactMarkdown
                              components={{
                                h1: ({ children }) => <h1 className="text-[#e7c887] font-bold text-base mb-2">{children}</h1>,
                                h2: ({ children }) => <h2 className="text-[#C6AC8E] font-bold text-sm mb-2">{children}</h2>,
                                h3: ({ children }) => <h3 className="text-[#C6AC8E] font-semibold text-sm mb-1">{children}</h3>,
                                strong: ({ children }) => <strong className="text-[#e7c887] font-bold">{children}</strong>,
                                em: ({ children }) => <em className="text-[#C6AC8E] italic">{children}</em>,
                                p: ({ children }) => <p className="text-[#EAE0D5] mb-2 leading-relaxed">{children}</p>,
                                ul: ({ children }) => <ul className="text-[#EAE0D5] list-disc list-inside mb-2 space-y-1">{children}</ul>,
                                ol: ({ children }) => <ol className="text-[#EAE0D5] list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                                li: ({ children }) => <li className="text-[#EAE0D5]">{children}</li>,
                                code: ({ children }) => <code className="bg-[#5E503F] text-[#e7c887] px-1 py-0.5 rounded text-xs">{children}</code>
                              }}
                            >
                              {currentProfileContent.rating.explanation}
                            </ReactMarkdown>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>

                {/* Category Summary */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-[#C6AC8E] mb-3">Summary</h4>
                  <div className="bg-[#22333B] rounded-lg p-4">
                    <div className="text-[#EAE0D5] leading-relaxed prose prose-invert prose-sm max-w-none kyp-markdown">
                      <ReactMarkdown
                        components={{
                          h1: ({ children }) => <h1 className="text-[#e7c887] font-bold text-xl mb-4">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-[#C6AC8E] font-bold text-lg mb-3">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-[#C6AC8E] font-semibold text-base mb-2">{children}</h3>,
                          h4: ({ children }) => <h4 className="text-[#C6AC8E] font-semibold text-sm mb-2">{children}</h4>,
                          h5: ({ children }) => <h5 className="text-[#C6AC8E] font-medium text-sm mb-1">{children}</h5>,
                          h6: ({ children }) => <h6 className="text-[#C6AC8E] font-medium text-xs mb-1">{children}</h6>,
                          strong: ({ children }) => <strong className="text-[#e7c887] font-bold">{children}</strong>,
                          em: ({ children }) => <em className="text-[#C6AC8E] italic">{children}</em>,
                          p: ({ children }) => <p className="text-[#EAE0D5] mb-3 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="text-[#EAE0D5] list-disc list-inside mb-3 space-y-1">{children}</ul>,
                          ol: ({ children }) => <ol className="text-[#EAE0D5] list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                          li: ({ children }) => <li className="text-[#EAE0D5]">{children}</li>,
                          blockquote: ({ children }) => <blockquote className="border-l-4 border-[#C6AC8E] pl-4 italic text-[#C6AC8E] my-3">{children}</blockquote>,
                          code: ({ children }) => <code className="bg-[#5E503F] text-[#e7c887] px-2 py-1 rounded text-sm">{children}</code>,
                          pre: ({ children }) => <pre className="bg-[#5E503F] text-[#EAE0D5] p-3 rounded overflow-x-auto">{children}</pre>
                        }}
                      >
                        {currentProfileContent.summary}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>

                {/* Article Summaries */}
                {currentSummaries.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-[#C6AC8E] mb-3">
                      Article References ({currentSummaries.length})
                    </h4>
                    
                    {isLoadingCurrentSummaries ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4E46FF]"></div>
                        <span className="ml-3 text-gray-400">Loading article references...</span>
                      </div>
                    ) : currentSummaries.length > 0 ? (
                      <div className="grid gap-3">
                        {currentSummaries.map((summary, index) => (
                          <motion.div
                            key={summary.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="border border-[#22333B] rounded-lg overflow-hidden"
                          >
                            <div className="bg-[#22333B] p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h5 className="font-medium text-[#EAE0D5] mb-1">
                                    Article {summary.article_order}
                                  </h5>
                                  <p className="text-sm text-[#C6AC8E] line-clamp-2">
                                    {summary.summary_text.substring(0, 150)}...
                                  </p>
                                  {summary.relevance_score && (
                                    <div className="mt-2">
                                      <span className="text-xs px-2 py-1 bg-gray-700 rounded text-gray-300">
                                        Relevance: {(summary.relevance_score * 100).toFixed(1)}%
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Action buttons row */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {summary.article_url && (
                                    <a
                                      href={summary.article_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3d37cc] hover:bg-[#4E46FF] text-white text-sm font-medium rounded transition-colors"
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" />
                                      View Original Article
                                    </a>
                                  )}
                                </div>
                                
                                <button
                                  onClick={() => toggleSummary(summary.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#5E503F] hover:bg-[#6B5A47] text-[#EAE0D5] text-sm font-medium rounded transition-colors"
                                >
                                  {expandedSummaries[summary.id] ? (
                                    <>
                                      <ChevronUp className="h-3.5 w-3.5" />
                                      Show Less
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3.5 w-3.5" />
                                      Show Full Summary
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                            
                            <AnimatePresence>
                              {expandedSummaries[summary.id] && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-4 bg-[#1A1A1A] border-t border-[#22333B]">
                                    <div className="text-sm text-[#EAE0D5] leading-relaxed mb-4 kyp-markdown">
                                      <ReactMarkdown
                                        components={{
                                          h1: ({ children }) => <h1 className="text-[#e7c887] font-bold text-lg mb-3">{children}</h1>,
                                          h2: ({ children }) => <h2 className="text-[#C6AC8E] font-bold text-base mb-2">{children}</h2>,
                                          h3: ({ children }) => <h3 className="text-[#C6AC8E] font-semibold text-sm mb-2">{children}</h3>,
                                          h4: ({ children }) => <h4 className="text-[#C6AC8E] font-semibold text-xs mb-1">{children}</h4>,
                                          strong: ({ children }) => <strong className="text-[#e7c887] font-bold">{children}</strong>,
                                          em: ({ children }) => <em className="text-[#C6AC8E] italic">{children}</em>,
                                          p: ({ children }) => <p className="text-[#EAE0D5] mb-2 leading-relaxed">{children}</p>,
                                          ul: ({ children }) => <ul className="text-[#EAE0D5] list-disc list-inside mb-2 space-y-1">{children}</ul>,
                                          ol: ({ children }) => <ol className="text-[#EAE0D5] list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                                          li: ({ children }) => <li className="text-[#EAE0D5]">{children}</li>,
                                          blockquote: ({ children }) => <blockquote className="border-l-4 border-[#C6AC8E] pl-3 italic text-[#C6AC8E] my-2">{children}</blockquote>,
                                          code: ({ children }) => <code className="bg-[#5E503F] text-[#e7c887] px-1 py-0.5 rounded text-xs">{children}</code>
                                        }}
                                      >
                                        {summary.summary_text}
                                      </ReactMarkdown>
                                    </div>
                                    
                                    {summary.sources && summary.sources.length > 0 && (
                                      <div>
                                        <h6 className="text-xs font-medium text-[#C6AC8E] mb-2">Sources:</h6>
                                        <div className="space-y-1">
                                          {summary.sources.map((source, sourceIndex) => (
                                            <a
                                              key={sourceIndex}
                                              href={source.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="block text-xs text-[#e7c887] hover:text-[#C6AC8E] hover:underline"
                                            >
                                              {source.title || source.url}
                                              {source.relevance_score && (
                                                <span className="text-[#5E503F] ml-2">
                                                  ({source.relevance_score})
                                                </span>
                                              )}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        No article references available for this category.
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ) : (
              /* Loading State for Incomplete Category */
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-6"
              >
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-400">
                      {formatCategoryName(selectedCategory)}
                    </h3>
                    <div className="px-3 py-1 rounded-full text-sm font-bold text-white bg-gray-600">
                      <Loader className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  {/* Loading Summary */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-400 mb-3">Summary</h4>
                    <div className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
                        <span className="ml-3 text-gray-500">Analyzing {formatCategoryName(selectedCategory).toLowerCase()}...</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Loading Article References */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-400 mb-3">
                      Article References
                    </h4>
                    <div className="text-center py-8 text-gray-500">
                      <Loader className="h-8 w-8 animate-spin mx-auto mb-3" />
                      <p>Processing articles and generating insights...</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
